const MIN_FREE_BYTES = 50 * 1024 * 1024;

export interface StorageStatus {
  persisted: boolean;
  estimateMB: number | null;
  lowSpace: boolean;
  /** True if the StorageManager API exists at all (Safari pre-15 missing). */
  apiAvailable: boolean;
}

export async function checkStorage(): Promise<StorageStatus> {
  if (typeof navigator === 'undefined' || !navigator.storage) {
    return { persisted: false, estimateMB: null, lowSpace: false, apiAvailable: false };
  }
  const persisted = (await navigator.storage.persisted?.()) ?? false;
  const estimate = (await navigator.storage.estimate?.()) ?? {};
  const free = (estimate.quota ?? 0) - (estimate.usage ?? 0);
  return {
    persisted,
    estimateMB: estimate.quota ? Math.floor(estimate.quota / (1024 * 1024)) : null,
    lowSpace: free > 0 && free < MIN_FREE_BYTES,
    apiAvailable: true,
  };
}

export type PersistResult =
  | { ok: true; persisted: true }
  | { ok: false; reason: 'denied' | 'unsupported' };

export async function requestPersist(): Promise<PersistResult> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) {
    return { ok: false, reason: 'unsupported' };
  }
  const persisted = await navigator.storage.persist();
  return persisted ? { ok: true, persisted: true } : { ok: false, reason: 'denied' };
}

/**
 * Lightweight wrapper around the deferred `beforeinstallprompt` event so
 * the React tree can subscribe + trigger the install prompt later. iOS
 * Safari does not fire this event at all — it requires the user to use
 * "Add to Home Screen" from the share sheet manually.
 */
type DeferredInstallPrompt = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferred: DeferredInstallPrompt | null = null;
const subs = new Set<(available: boolean) => void>();

export function bindInstallPrompt(): () => void {
  if (typeof window === 'undefined') return () => {};
  const onBefore = (e: Event) => {
    e.preventDefault();
    deferred = e as DeferredInstallPrompt;
    for (const s of subs) s(true);
  };
  const onInstalled = () => {
    deferred = null;
    for (const s of subs) s(false);
  };
  window.addEventListener('beforeinstallprompt', onBefore);
  window.addEventListener('appinstalled', onInstalled);
  return () => {
    window.removeEventListener('beforeinstallprompt', onBefore);
    window.removeEventListener('appinstalled', onInstalled);
  };
}

export function subscribeInstallAvailable(fn: (available: boolean) => void): () => void {
  subs.add(fn);
  fn(deferred !== null);
  return () => subs.delete(fn);
}

export type InstallResult =
  | { outcome: 'accepted' | 'dismissed' }
  | { outcome: 'unavailable' };

export async function triggerInstallPrompt(): Promise<InstallResult> {
  if (!deferred) return { outcome: 'unavailable' };
  await deferred.prompt();
  const choice = await deferred.userChoice;
  deferred = null;
  for (const s of subs) s(false);
  return { outcome: choice.outcome };
}

/** True if running as an installed PWA (display-mode standalone). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS Safari home-screen apps expose navigator.standalone === true.
  const navStandalone = (navigator as Navigator & { standalone?: boolean }).standalone;
  return navStandalone === true;
}
